// Pusat Kendali Owner — Tahap 1: enam kartu pengawasan. Hanya membaca API yang sudah ada; klik membuka halaman yang sudah ada.
// Tahap 2: angka per kanal (Offline / Shopee / Tokopedia) dari pemisahan backend yang terverifikasi; tidak dijumlahkan antar kanal.
import { useState } from "react";
import { StaffDetail, StockDetail, SystemDetail, CustomerDetail, sysIssues, sysOngoing, useOwnerGet as useGet, manualCutTotal, chans } from "./owner_detail_panels";
import { Factory, Users, Boxes, ShieldAlert, Wallet, MessageSquareWarning, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NA = "Belum tersedia";
const rp = (n) => "Rp" + Math.round(Number(n) || 0).toLocaleString("id-ID");

function Card({ icon: Icon, title, onOpen, testid, line, issue, expanded }) {
  return <button type="button" className={`occw-card${expanded ? " is-open" : ""}`} onClick={onOpen} data-testid={testid} aria-expanded={expanded}>
    <Icon size={22} className="occw-ic" />
    <span className="occw-body"><b>{title}</b><span className={`occw-line ${issue ? "is-issue" : ""}`} data-testid={`${testid}-line`}>{line}</span></span>
    <ChevronRight size={20} className="occw-go" />
  </button>;
}

// Kalimat terpenting: masalah pertama yang > 0 menurut prioritas; bila tidak ada → kalimat netral.
export function headline(items, calm) {
  const hit = items.find(([n]) => typeof n === "number" && n > 0);
  return hit ? { line: `${hit[0]} ${hit[1]}`, issue: true } : { line: calm, issue: false };
}

// Urutan kartu: yang punya masalah merah dulu, lalu kuning, lalu sisanya (urutan bawaan tetap).
export function sortCards(cards) {
  const rank = (c) => (c.red > 0 ? 0 : c.gold > 0 ? 1 : 2);
  return cards.map((c, i) => ({ ...c, i })).sort((a, b) => rank(a) - rank(b) || a.i - b.i);
}

export function OwnerWatchCards({ data, go, onReceivable, channel = "all" }) {
  const stock = useGet(`${API}/stock/summary`);
  const cuts = useGet(`${API}/acc-stock-cut/settings`);
  const [detail, setDetail] = useState(null);
  const toggle = (k) => setDetail(d => (d === k ? null : k));
  const shp = !!data.shopee_available;
  const on = (k) => channel === "all" || channel === k;
  const off = data.offline_late || {}, tkp = data.tokopedia_late || {};
  const risk = data.shopee_risk || {};
  const fu = Object.fromEntries((data.followups || []).map(f => [f.key, f.count]));
  // Hanya kanal yang dipilih; null = tidak ditampilkan (bukan 0). Kanal tidak pernah dijumlahkan.
  const pick = (k, n) => (on(k) ? n || 0 : null);
  const sum = (xs) => xs.reduce((a, [n]) => a + (n || 0), 0);
  const rv = data.bordir_review || {};
  const rvKeys = channel === "all" ? Object.keys(rv) : [channel];
  const nReview = data.bordir_review ? rvKeys.reduce((a, c) => a + ["claims", "finish_requests", "unregistered"].reduce((b, k) => b + ((rv[c] || {})[k] || 0), 0), 0) : null;
  // Stok: hanya pesanan online yang punya Pengecekan Stok Mockup; Offline belum tersedia (tidak ditulis 0).
  const sc = chans(channel).filter(c => c !== "offline").map(c => (data.stock || {})[c]).filter(Boolean);
  const nShort = data.stock && sc.length ? sc.reduce((a, x) => a + x.short_count, 0) : null;
  const nPend = data.stock && sc.length ? sc.reduce((a, x) => a + x.pending_count, 0) : null;
  const nCuts = channel === "all" ? manualCutTotal(cuts) : null;
  const stockCalm = channel === "offline" ? "Pengecekan stok Offline belum tersedia" : "Tidak ada kekurangan stok terverifikasi";
  const loading = { line: "Memuat…", issue: false };
  const mon = channel === "shopee" ? ["shopee-monitor", {}] : channel === "tokopedia" ? ["shopee-monitor", { channel: "tokopedia" }] : ["offline-monitor", {}];

  const prodRed = [[pick("offline", off.overdue), "pesanan Offline terlambat"], [shp ? pick("shopee", risk.overdue) : null, "pesanan Shopee lewat batas kirim"],
    [pick("tokopedia", tkp.overdue), "pesanan Tokopedia lewat batas kirim"]];
  const prodGold = [[pick("shopee", fu.shp_blocked), "pesanan Shopee menunggu ACC Gudang"], [pick("tokopedia", fu.tkp_blocked), "pesanan Tokopedia menunggu ACC Gudang"]];
  const pic = [[pick("offline", fu.off_unassigned), "pekerjaan Offline perlu PIC"], [pick("shopee", fu.shp_unassigned), "pekerjaan Shopee perlu PIC"],
    [pick("tokopedia", fu.tkp_unassigned), "pekerjaan Tokopedia perlu PIC"]];
  const cu = data.customer || {};
  const csum = (k) => (data.customer ? chans(channel).concat(channel === "all" ? ["unknown"] : []).reduce((a, c) => a + ((cu[c] || {})[k] || 0), 0) : null);
  const hold = [[pick("shopee", fu.hold_pending_shopee), "Tahan/Batal Shopee menunggu keputusan"],
    [pick("tokopedia", fu.hold_pending_tokopedia), "Tahan/Batal Tokopedia menunggu keputusan"],
    [shp ? pick("shopee", cu.shopee?.returns?.active_count) : null, "retur Shopee menunggu tindak lanjut"],
    [shp ? pick("shopee", fu.in_cancel) : null, "pengajuan batal pembeli Shopee"],
    [channel === "offline" ? null : csum("revision_stalled"), "revisi mockup tertahan"],
    [csum("issue_count"), "kendala produksi internal terbuka"]];
  const fin = channel === "tokopedia" ? { line: NA, issue: false }
    : channel === "shopee" ? { line: shp && data.health?.shopee ? `Nilai pesanan 7 hari ${rp(data.health.shopee.d7.order_value)}` : NA, issue: false }
    : { line: data.finance ? `Piutang ${rp(data.finance.offline_receivable)}` : NA, issue: false };

  // Sistem: hanya gangguan yang masih berlangsung yang menaikkan kartu; tanpa log tidak pernah ditulis "aman".
  const ong = sysOngoing(sysIssues(data.system, channel));
  const stops = ong.filter(i => i.severity === "stop");
  const sys = !data.system ? { line: NA, issue: false, red: 0, gold: 0 }
    : ong.length ? { line: `${ong.length} gangguan berlangsung: ${(stops[0] || ong[0]).title}`, issue: true, red: stops.length, gold: ong.length - stops.length }
    : { line: "Tidak ada gangguan tercatat · sebagian belum dicatat sistem", issue: false, red: 0, gold: 0 };

  const cards = [
    { key: "produksi", icon: Factory, title: "Produksi & Pengiriman", open: () => go(...mon),
      red: sum(prodRed), gold: sum(prodGold), ...headline([...prodRed, ...prodGold], "Tidak ada pesanan terlambat") },
    { key: "karyawan", icon: Users, title: "Karyawan", open: () => toggle("karyawan"),
      red: 0, gold: sum(pic) + (nReview || 0),
      ...headline([...pic, [nReview, "hasil Bordir perlu pemeriksaan Owner"]], "Tidak ada pekerjaan perlu PIC") },
    { key: "stok", icon: Boxes, title: "Stok & Bahan", open: () => toggle("stok"),
      red: nShort || 0, gold: (nPend || 0) + (nCuts || 0),
      ...(!data.stock && channel !== "offline" ? { line: NA, issue: false }
        : channel === "all" && cuts === undefined && !nShort && !nPend ? loading
        : headline([[nShort, "barang kurang/habis dibutuhkan pesanan aktif"], [nPend, "pesanan belum bisa dicek stoknya"],
          [nCuts, "potong stok otomatis perlu ditangani manual"]], stockCalm)) },
    { key: "sistem", icon: ShieldAlert, title: "Sistem & Error", open: () => toggle("sistem"), ...sys },
    { key: "keuangan", icon: Wallet, title: "Penjualan & Keuangan", open: onReceivable, red: 0, gold: 0, ...fin },
    { key: "pelanggan", icon: MessageSquareWarning, title: "Pelanggan & Komplain", open: () => toggle("pelanggan"),
      red: 0, gold: sum(hold), ...headline(hold, channel === "offline" ? "Tidak ada kendala terbuka · komplain Offline belum dicatat" : "Tidak ada Tahan/Batal menunggu keputusan") },
  ];
  return <section className="occw" data-testid="occ-watch-cards">
    <h3 className="occw-title">Pengawasan Owner</h3>
    <div className="occw-list">
      {sortCards(cards).map(c => <Card key={c.key} icon={c.icon} title={c.title} onOpen={c.open} line={c.line} issue={c.issue} testid={`occ-watch-${c.key}`}
        expanded={["karyawan", "stok", "sistem", "pelanggan"].includes(c.key) ? detail === c.key : undefined} />)}
    </div>
    {detail === "karyawan" && <StaffDetail data={data} channel={channel} go={go} onClose={() => setDetail(null)}/>}
    {detail === "pelanggan" && <CustomerDetail data={data} channel={channel} go={go} onClose={() => setDetail(null)}/>}
    {detail === "sistem" && <SystemDetail data={data} channel={channel} go={go} onClose={() => setDetail(null)}/>}
    {detail === "stok" && <StockDetail data={data} channel={channel} go={go} onClose={() => setDetail(null)} stockSummary={stock} manualCuts={cuts}/>}
  </section>;
}
